import { useState, useEffect } from 'react';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { PiggyBank, Wallet, Heart } from 'lucide-react';

/**
 * AllocationSlider - A reusable component for managing Spend/Save/Give allocation percentages
 * @param {Object} props
 * @param {number} props.spendPercent - Initial spend percentage
 * @param {number} props.savePercent - Initial save percentage  
 * @param {number} props.givePercent - Initial give percentage
 * @param {Function} props.onChange - Callback when allocation changes: ({spend, save, give}) => void
 * @param {boolean} props.disabled - Disable all sliders
 * @param {boolean} props.compact - Use compact layout
 */
export const AllocationSlider = ({ 
  spendPercent = 70, 
  savePercent = 20, 
  givePercent = 10,
  onChange,
  disabled = false,
  compact = false
}) => {
  const [allocation, setAllocation] = useState({
    spend: spendPercent,
    save: savePercent,
    give: givePercent
  });

  useEffect(() => {
    setAllocation({
      spend: spendPercent,
      save: savePercent,
      give: givePercent
    });
  }, [spendPercent, savePercent, givePercent]);

  const handleChange = (type, value) => {
    const newValue = value[0];
    const oldValue = allocation[type];
    const diff = newValue - oldValue;
    
    // Calculate remaining to distribute
    const others = Object.keys(allocation).filter(k => k !== type);
    const othersTotal = others.reduce((sum, k) => sum + allocation[k], 0);
    
    if (othersTotal === 0 && diff > 0) return; // Can't increase if others are 0
    
    // Distribute the difference proportionally to other sliders
    const newAllocation = { ...allocation, [type]: newValue };
    
    if (othersTotal > 0) {
      others.forEach(other => {
        const proportion = allocation[other] / othersTotal;
        newAllocation[other] = Math.max(0, Math.round(allocation[other] - diff * proportion));
      });
    }
    
    // Ensure total is exactly 100
    const total = Object.values(newAllocation).reduce((a, b) => a + b, 0);
    if (total !== 100) {
      // Adjust the largest other value to compensate
      const adjustKey = others.reduce((a, b) => newAllocation[a] >= newAllocation[b] ? a : b);
      newAllocation[adjustKey] += (100 - total);
    }
    
    // Clamp all values between 0 and 100
    Object.keys(newAllocation).forEach(k => {
      newAllocation[k] = Math.max(0, Math.min(100, newAllocation[k]));
    });
    
    setAllocation(newAllocation);
    onChange?.({
      spend: newAllocation.spend,
      save: newAllocation.save,
      give: newAllocation.give
    });
  };

  const sliderConfig = [
    { 
      key: 'spend', 
      label: 'Spend', 
      icon: Wallet, 
      color: 'bg-blue-500',
      trackColor: 'bg-blue-100',
      textColor: 'text-blue-600'
    },
    { 
      key: 'save', 
      label: 'Save', 
      icon: PiggyBank, 
      color: 'bg-emerald-500',
      trackColor: 'bg-emerald-100',
      textColor: 'text-emerald-600'
    },
    { 
      key: 'give', 
      label: 'Give', 
      icon: Heart, 
      color: 'bg-pink-500',
      trackColor: 'bg-pink-100',
      textColor: 'text-pink-600'
    }
  ];

  if (compact) {
    return (
      <div className="space-y-3">
        {sliderConfig.map(({ key, label, icon: Icon, color, textColor }) => (
          <div key={key} className="flex items-center gap-3">
            <div className={`flex items-center gap-1.5 w-20 ${textColor}`}>
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{label}</span>
            </div>
            <div className="flex-1">
              <Slider
                value={[allocation[key]]}
                onValueChange={(v) => handleChange(key, v)}
                max={100}
                step={5}
                disabled={disabled}
                className={`[&_[role=slider]]:${color}`}
                data-testid={`allocation-${key}-slider`}
              />
            </div>
            <span className={`w-12 text-right text-sm font-semibold ${textColor}`}>
              {allocation[key]}%
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sliderConfig.map(({ key, label, icon: Icon, color, trackColor, textColor }) => (
        <div key={key} className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className={`flex items-center gap-2 ${textColor}`}>
              <Icon className="w-4 h-4" />
              {label}
            </Label>
            <span className={`text-lg font-bold ${textColor}`}>
              {allocation[key]}%
            </span>
          </div>
          <Slider
            value={[allocation[key]]}
            onValueChange={(v) => handleChange(key, v)}
            max={100}
            step={5}
            disabled={disabled}
            className={`[&_[role=slider]]:${color} [&_[role=slider]]:border-0`}
            data-testid={`allocation-${key}-slider`}
          />
        </div>
      ))}
      <div className="flex justify-center pt-2">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500" /> Spend
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-emerald-500" /> Save
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-pink-500" /> Give
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * AllocationPreview - Shows how an amount would be distributed
 */
export const AllocationPreview = ({ amount, spend, save, give }) => {
  if (!amount || amount <= 0) return null;
  
  const spendAmount = (amount * spend / 100).toFixed(2);
  const saveAmount = (amount * save / 100).toFixed(2);
  const giveAmount = (amount * give / 100).toFixed(2);

  return (
    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
      <p className="text-xs text-slate-500 font-medium">Distribution Preview</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-blue-50 rounded-lg p-2">
          <Wallet className="w-4 h-4 text-blue-500 mx-auto mb-1" />
          <p className="text-xs text-slate-500">Spend</p>
          <p className="text-sm font-bold text-blue-600">${spendAmount}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2">
          <PiggyBank className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
          <p className="text-xs text-slate-500">Save</p>
          <p className="text-sm font-bold text-emerald-600">${saveAmount}</p>
        </div>
        <div className="bg-pink-50 rounded-lg p-2">
          <Heart className="w-4 h-4 text-pink-500 mx-auto mb-1" />
          <p className="text-xs text-slate-500">Give</p>
          <p className="text-sm font-bold text-pink-600">${giveAmount}</p>
        </div>
      </div>
    </div>
  );
};

export default AllocationSlider;
